import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import moment from 'moment';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Alert } from '@material-ui/lab';
import styled from 'styled-components';
import {
  DeleteRounded as DeleteIcon,
  AddRounded as AddIcon,
  EditRounded as EditIcon
} from '@material-ui/icons';

import {
  AuthContext,
  baseUrl,
  formatNumber,
  getOrgFeatures,
  isOrgReadOnly,
  isConcreteHolidayDate,
  localizeLeaveType,
  publicHolidayDateForYear,
  uploadFileToSignedUrl,
  useFile,
  useSpot,
  validateEmail
} from 'framework';
import {
  FormTextInput,
  FormLabel,
  FormRow,
  Container,
  Row,
  CroppedImage,
  FormNumberInput,
  TitleContainer,
  Capitalized,
  Line,
  FormFieldWrapper,
  Tabs,
  Table,
  Spacer,
  PublicHolidayResult,
  EditPublicHolidayDialog,
  ConfirmDialog,
  Gap,
  PermanentlyDeleteDialog,
  SettingsPanel,
  SettingsPanelOptions,
  SettingsToggleOption
} from 'components';
import {
  HolidayProfile,
  OnboardingRequiredDocumentType,
  Org
} from 'domain-types';
import { OrgAdminApplicationsTab } from './org-admin-applications';
import { OrgAdminAuthenticationTab } from './org-admin-authentication';
import {
  createHolidayProfile,
  getConfiguredHolidayProfile
} from './holiday-profile';

const InputPostText = styled(Typography)`
  padding: ${p => p.theme.spacing(0, 1)};
  white-space: nowrap;
`;

const StyledButton = styled(Button)`
  margin: ${p => p.theme.spacing(2, 2)};
  padding: ${p => p.theme.spacing(2)}px;
`;

const CopyValueButton = styled(Button)`
  min-width: 88px;
`;

const InlineFieldRow = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
`;

const FieldColumn = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const FieldHelpText = styled(Typography)`
  padding: ${p => p.theme.spacing(0, 1)};
`;

const DEFAULT_NEW_LEAVE_TYPE_ALLOWANCE_DAYS = 365;

const validateExpenseIdPrefix = (value: string) => {
  const normalized = value.trim();

  if (normalized.length === 0) {
    return true;
  }

  return normalized.length <= 12 && /^[A-Za-z0-9#_-]+$/.test(normalized);
};

interface AddPublicHolidayDialogDesc {
  name?: string;
  date?: string;
  callback: (publicHoliday: PublicHolidayResult) => Promise<void>;
}

interface ConfirmDialogDesc {
  title?: string;
  text?: string;
  callback: () => Promise<void>;
}

interface DeleteDialogDesc {
  callback: () => Promise<void>;
}

interface HolidayCalendarFeed {
  configured: boolean;
  url: string;
}

const regenerateHolidayCalendarFeed = async (
  organization: string,
  jwtToken: string
) => {
  const response = await fetch(
    `${baseUrl}/org/${organization}/holiday-calendar-feed`,
    {
      headers: {
        authorization: jwtToken
      },
      method: 'POST'
    }
  );

  if (!response.ok) {
    throw new Error('Failed to regenerate holiday calendar feed');
  }

  return (await response.json()) as HolidayCalendarFeed;
};

export function OrgAdmin() {
  const fileInput = useRef<HTMLInputElement>(null);
  const copyResetTimeout = useRef<number | null>(null);
  const { getToken } = useContext(AuthContext);
  const { organization, tab } = useParams<{
    organization: string;
    tab?: string;
  }>();

  const history = useHistory();
  const location = useLocation();

  const handleTabChange = (selectedTab: string) => {
    history.replace(`/${organization}/admin/${selectedTab}`);
  };

  const [
    publicHolidayDialog,
    setPublicHolidayDialog
  ] = useState<AddPublicHolidayDialogDesc | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogDesc | null>(
    null
  );

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogDesc | null>(
    null
  );
  const [newLeaveTypeName, setNewLeaveTypeName] = useState('');
  const [
    holidayCalendarFeed,
    setHolidayCalendarFeed
  ] = useState<HolidayCalendarFeed | null>(null);
  const [holidayCalendarFeedPending, setHolidayCalendarFeedPending] = useState(
    false
  );
  const [billingRestorePending, setBillingRestorePending] = useState(false);
  const [copiedHolidayCalendarUrl, setCopiedHolidayCalendarUrl] = useState(
    false
  );
  const [iconUploadError, setIconUploadError] = useState('');

  const { t } = useTranslation();

  const { data, errors, query, command, loading } = useSpot();

  useEffect(() => {
    if (errors.find(err => err.status === 401)) {
      window.location.reload();
    }
  }, [errors]);

  useEffect(
    () => () => {
      if (copyResetTimeout.current) {
        window.clearTimeout(copyResetTimeout.current);
      }
    },
    []
  );

  useEffect(() => {
    (async () => {
      await query(`org/${organization}`, {}, ['organizations', organization]);
      await query(`org/${organization}/subscriptions`, {}, [
        'organizations',
        organization,
        'subscriptions'
      ]);
    })();
  }, [organization, query]);

  const org = data?.organizations?.[organization];
  const readOnly = isOrgReadOnly(org);
  const billingRestoreResult = new URLSearchParams(location.search).get(
    'billingRestore'
  );
  const onboardingRequiredDocuments = org?.onboardingRequiredDocuments ?? [];
  const orgFeatures = getOrgFeatures(org);
  const publicHolidayDateReference = moment();
  const icon = org?.icon;
  const holidayProfile = getConfiguredHolidayProfile(org?.holidayProfile);
  const icsCalendarUrl = holidayCalendarFeed?.url ?? org?.icsCalendarUrl ?? '';
  const hasIcsCalendarUrl =
    holidayCalendarFeed?.configured ?? !!org?.icsCalendarUrl;

  const updateOrgFields = async (fields: Partial<Org>) => {
    if (readOnly) {
      return;
    }

    await command(`org/${organization}`, fields);
    await query(`org/${organization}`, {}, ['organizations', organization]);
  };

  const updateOrg = async (
    field: string,
    value:
      | string
      | number
      | boolean
      | string[]
      | { [k: string]: number }
      | Org['features']
      | Org['googleOidc']
      | Org['microsoftOidc']
      | HolidayProfile
      | null
  ) => {
    await updateOrgFields({
      [field]: value
    } as Partial<Org>);
  };

  const updateGoogleOidc = async (
    fields: Partial<NonNullable<Org['googleOidc']>>
  ) => {
    await updateOrg('googleOidc', {
      ...(org?.googleOidc || {}),
      ...fields
    });
  };

  const updateMicrosoftOidc = async (
    fields: Partial<NonNullable<Org['microsoftOidc']>>
  ) => {
    await updateOrg('microsoftOidc', {
      ...(org?.microsoftOidc || {}),
      ...fields
    });
  };

  const toggleOnboardingRequiredDocument = async (
    document: OnboardingRequiredDocumentType,
    enabled: boolean
  ) => {
    const nextDocuments = enabled
      ? [...onboardingRequiredDocuments, document]
      : onboardingRequiredDocuments.filter(
          currentDocument => currentDocument !== document
        );

    await updateOrg('onboardingRequiredDocuments', nextDocuments);
  };

  const toggleFeature = async (
    feature: keyof typeof orgFeatures,
    enabled: boolean
  ) => {
    await updateOrg('features', {
      ...orgFeatures,
      [feature]: enabled
    });
  };

  const uploadIcon = async (file: File) => {
    if (!file || readOnly) {
      return;
    }

    setIconUploadError('');

    try {
      await uploadFileToSignedUrl(file, organization, getToken().getJwtToken());

      await command(`org/${organization}`, {
        icon: `${file.name}`
      });

      await query(`org/${organization}`, {}, ['organizations', organization]);
    } catch (_err) {
      setIconUploadError(t('orgLogoUploadFailed'));
    }
  };

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.files?.length && uploadIcon(e.target.files[0]);
    e.target.value = '';
  };

  const onDeletePublicHoliday = (id: string) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      title: t('confirmPublicHolidayDeleteTitle'),
      text: t('confirmPublicHolidayDeleteSubtitle'),
      callback: async () => {
        await command(
          `org/${organization}/publicHoliday/${id}`,
          {},
          { method: 'DELETE' }
        );
        await query(`org/${organization}`, {}, ['organizations', organization]);
        setConfirmDialog(null);
      }
    });
  };

  const generateHolidayCalendarFeed = async () => {
    if (readOnly) {
      return;
    }

    setHolidayCalendarFeedPending(true);

    try {
      const response = await regenerateHolidayCalendarFeed(
        organization,
        getToken().getJwtToken()
      );

      setHolidayCalendarFeed(response);
      await query(`org/${organization}`, {}, ['organizations', organization]);
      setCopiedHolidayCalendarUrl(false);
    } finally {
      setHolidayCalendarFeedPending(false);
    }
  };

  const copyHolidayCalendarUrl = async () => {
    if (!icsCalendarUrl) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(icsCalendarUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = icsCalendarUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedHolidayCalendarUrl(true);
      if (copyResetTimeout.current) {
        window.clearTimeout(copyResetTimeout.current);
      }
      copyResetTimeout.current = window.setTimeout(
        () => setCopiedHolidayCalendarUrl(false),
        2000
      );
    } catch {
      setCopiedHolidayCalendarUrl(false);
    }
  };

  const onRegenerateHolidayCalendarFeed = () => {
    setConfirmDialog({
      title: t('regenerateIcsCalendarUrlTitle'),
      text: t('regenerateIcsCalendarUrlSubtitle'),
      callback: async () => {
        await generateHolidayCalendarFeed();
        setConfirmDialog(null);
      }
    });
  };

  const onAddPublicHoliday = () => {
    if (readOnly) {
      return;
    }

    setPublicHolidayDialog({
      callback: async publicHoliday => {
        await command(`org/${organization}/publicHoliday`, {
          ...publicHoliday
        });
        await query(`org/${organization}`, {}, ['organizations', organization]);
        setPublicHolidayDialog(null);
      }
    });
  };

  const onEditPublicHoliday = (id: string, date: string, name: string) => {
    if (readOnly) {
      return;
    }

    setPublicHolidayDialog({
      name,
      date,
      callback: async publicHoliday => {
        await command(`org/${organization}/publicHoliday/${id}`, {
          ...publicHoliday
        });
        await query(`org/${organization}`, {}, ['organizations', organization]);
        setPublicHolidayDialog(null);
      }
    });
  };

  const onDeleteOrganization = () => {
    if (readOnly) {
      return;
    }

    setDeleteDialog({
      callback: async () => {
        await command(`org/${organization}`, {}, { method: 'DELETE' });
        setConfirmDialog(null);
        window.location.href = `https://spothr.net`;
      }
    });
  };

  const onRestoreBilling = async () => {
    setBillingRestorePending(true);

    try {
      const response = (await command(
        `org/${organization}/billing/restore`,
        {}
      )) as {
        url?: string;
      };

      if (response?.url) {
        window.location.href = response.url;
      }
    } finally {
      setBillingRestorePending(false);
    }
  };

  const { url: imageUrl } = useFile(icon || '', organization);
  const normalizedNewLeaveTypeName = newLeaveTypeName.trim();
  const orgLeaveTypes = org?.leaveTypes;
  const hasLoadedLeaveTypes = orgLeaveTypes !== undefined;
  const leaveTypeExists = Object.keys(orgLeaveTypes ?? {}).some(
    type => type.toLowerCase() === normalizedNewLeaveTypeName.toLowerCase()
  );
  const leaveTypeCount = orgLeaveTypes ? Object.keys(orgLeaveTypes).length : 0;
  const shouldKeepAtLeastOneLeaveType =
    hasLoadedLeaveTypes && leaveTypeCount <= 1;

  const onAddLeaveType = async () => {
    if (
      !hasLoadedLeaveTypes ||
      !normalizedNewLeaveTypeName ||
      leaveTypeExists
    ) {
      return;
    }

    await updateOrg('leaveTypes', {
      ...orgLeaveTypes,
      [normalizedNewLeaveTypeName]: DEFAULT_NEW_LEAVE_TYPE_ALLOWANCE_DAYS
    });
    setNewLeaveTypeName('');
  };

  const onDeleteLeaveType = async (type: string) => {
    if (!orgLeaveTypes) {
      return;
    }

    const nextLeaveTypes = Object.entries(orgLeaveTypes).reduce(
      (acc, [currentType, hours]) => {
        if (currentType !== type) {
          acc[currentType] = hours;
        }
        return acc;
      },
      {} as { [k: string]: number }
    );

    await updateOrgFields({
      leaveTypes: nextLeaveTypes,
      disabledLeaveTypes: (org?.disabledLeaveTypes ?? []).filter(
        currentType => currentType !== type
      ),
      nonDeductibleLeaveTypes: (org?.nonDeductibleLeaveTypes ?? []).filter(
        currentType => currentType !== type
      )
    });
  };

  const tabs = {
    admin: (
      <>
        {readOnly ? (
          <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
        ) : null}
        <Line />
        <Row>
          <Container>
            <FormRow>
              <FormLabel>{`${t('name')}:`}</FormLabel>
              <FormTextInput
                initialValue={org?.name}
                onChange={(value: string) => updateOrg('name', value)}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{`${t('description')}:`}</FormLabel>
              <FormTextInput
                multiline
                rows={3}
                rowsMax={3}
                rowsMin={3}
                initialValue={org?.description}
                onChange={(value: string) => updateOrg('description', value)}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{`${t('ownerEmail')}:`}</FormLabel>
              <FormTextInput
                initialValue={org?.ownerEmail}
                validate={validateEmail}
                onChange={(value: string) => updateOrg('ownerEmail', value)}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{`${t('icsCalendarUrl')}:`}</FormLabel>
              <FieldColumn>
                <InlineFieldRow>
                  <FormTextInput
                    initialValue={icsCalendarUrl}
                    inputProps={{ readOnly: true }}
                    placeholder={t('icsCalendarUrlNotConfigured')}
                  />
                  {hasIcsCalendarUrl && (
                    <CopyValueButton
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={copyHolidayCalendarUrl}
                    >
                      {copiedHolidayCalendarUrl ? t('copied') : t('copyValue')}
                    </CopyValueButton>
                  )}
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={
                      hasIcsCalendarUrl
                        ? onRegenerateHolidayCalendarFeed
                        : generateHolidayCalendarFeed
                    }
                    disabled={holidayCalendarFeedPending || readOnly}
                  >
                    {hasIcsCalendarUrl
                      ? t('regenerateIcsCalendarUrl')
                      : t('generateIcsCalendarUrl')}
                  </Button>
                </InlineFieldRow>
                <FieldHelpText variant="caption">
                  {t('icsCalendarUrlHelpText')}
                </FieldHelpText>
              </FieldColumn>
            </FormRow>
            <FormRow>
              <FormLabel>{`${t('birthdayCalendarSettingLabel')}:`}</FormLabel>
              <Switch
                checked={!!org?.birthdayCalendarEnabled}
                disabled={readOnly || holidayCalendarFeedPending}
                onChange={event =>
                  updateOrg('birthdayCalendarEnabled', event.target.checked)
                }
                inputProps={{
                  'aria-label': t('birthdayCalendarSettingLabel')
                }}
              />
            </FormRow>
            <Gap />
            <TitleContainer>
              <Capitalized variant="h6">
                {t('onboardingRequiredDocumentsSetting')}
              </Capitalized>
            </TitleContainer>
            <Typography variant="body2">
              {t('onboardingRequiredDocumentsSettingDescription')}
            </Typography>
            <Line />
            <FormRow>
              <FormLabel>{`${t('onboardingRequiredDocumentId')}:`}</FormLabel>
              <Switch
                checked={onboardingRequiredDocuments.includes('id')}
                disabled={readOnly}
                onChange={event =>
                  toggleOnboardingRequiredDocument('id', event.target.checked)
                }
                inputProps={{
                  'aria-label': t('onboardingRequiredDocumentId')
                }}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{`${t('onboardingRequiredDocumentSignedContract')}:`}</FormLabel>
              <Switch
                checked={onboardingRequiredDocuments.includes(
                  'signed_contract'
                )}
                disabled={readOnly}
                onChange={event =>
                  toggleOnboardingRequiredDocument(
                    'signed_contract',
                    event.target.checked
                  )
                }
                inputProps={{
                  'aria-label': t('onboardingRequiredDocumentSignedContract')
                }}
              />
            </FormRow>
          </Container>
          <Box>
            {iconUploadError ? (
              <Alert severity="error">{iconUploadError}</Alert>
            ) : null}
            <StyledButton
              variant="outlined"
              onClick={() => fileInput.current?.click()}
              disabled={readOnly}
            >
              <CroppedImage
                width={256}
                height={256}
                url={imageUrl}
                alt={org?.name}
              />
            </StyledButton>
            <input
              ref={fileInput}
              type="file"
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
          </Box>
        </Row>
      </>
    ),
    features: (
      <>
        {readOnly ? (
          <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
        ) : null}
        <FormRow>
          <FieldColumn>
            <SettingsPanel
              title={t('featureSettings')}
              description={t('featureSettingsDescription')}
            >
              <SettingsPanelOptions>
                <SettingsToggleOption
                  control={
                    <Switch
                      checked={orgFeatures.apps}
                      disabled={readOnly}
                      onChange={event =>
                        toggleFeature('apps', event.target.checked)
                      }
                    />
                  }
                  label={t('orgFeatureApps')}
                />
                <SettingsToggleOption
                  control={
                    <Switch
                      checked={orgFeatures.leave}
                      disabled={readOnly}
                      onChange={event =>
                        toggleFeature('leave', event.target.checked)
                      }
                    />
                  }
                  label={t('orgFeatureLeave')}
                />
                <SettingsToggleOption
                  control={
                    <Switch
                      checked={orgFeatures.expenses}
                      disabled={readOnly}
                      onChange={event =>
                        toggleFeature('expenses', event.target.checked)
                      }
                    />
                  }
                  label={t('orgFeatureExpenses')}
                />
                <SettingsToggleOption
                  control={
                    <Switch
                      checked={orgFeatures.mcp}
                      disabled={readOnly}
                      onChange={event =>
                        toggleFeature('mcp', event.target.checked)
                      }
                    />
                  }
                  label={t('orgFeatureMcp')}
                />
              </SettingsPanelOptions>
            </SettingsPanel>
          </FieldColumn>
        </FormRow>
      </>
    ),
    ...(orgFeatures.apps
      ? {
          applications: (
            <OrgAdminApplicationsTab
              organization={organization}
              org={org}
              readOnly={readOnly}
              updateOrgFields={updateOrgFields}
            />
          )
        }
      : {}),
    authentication: (
      <>
        {readOnly ? (
          <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
        ) : null}
        <OrgAdminAuthenticationTab
          organization={organization}
          org={org}
          onSelectAuthProvider={provider => updateOrg('authProvider', provider)}
          onUpdateGoogleOidc={updateGoogleOidc}
          onUpdateMicrosoftOidc={updateMicrosoftOidc}
        />
      </>
    ),
    ...(orgFeatures.leave
      ? {
          leaveConfig: (
            <>
              {readOnly ? (
                <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
              ) : null}
              <Typography variant="body2">
                {t('leaveTypesDescription')}
              </Typography>
              <Line />
              <FormRow>
                <FormLabel>{`${t('addLeaveType')}:`}</FormLabel>
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ gap: 8 }}
                >
                  <TextField
                    value={newLeaveTypeName}
                    onChange={event => setNewLeaveTypeName(event.target.value)}
                    placeholder={t('enterLeaveTypeName')}
                    disabled={!hasLoadedLeaveTypes}
                    error={!!normalizedNewLeaveTypeName && leaveTypeExists}
                    helperText={
                      !!normalizedNewLeaveTypeName && leaveTypeExists
                        ? t('leaveTypeAlreadyExists')
                        : ' '
                    }
                    inputProps={{
                      'aria-label': t('addLeaveType')
                    }}
                  />
                  <Button
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onAddLeaveType}
                    disabled={
                      readOnly ||
                      !hasLoadedLeaveTypes ||
                      !normalizedNewLeaveTypeName ||
                      leaveTypeExists
                    }
                  >
                    {t('addLeaveType')}
                  </Button>
                </Box>
              </FormRow>
              {Object.entries(org?.leaveTypes ?? {})?.map(([type, hours]) => (
                <Box key={type}>
                  <FormRow>
                    <FormLabel>{`${localizeLeaveType(t, type)}:`}</FormLabel>
                    <Box display="flex">
                      <FormNumberInput
                        initialValue={hours}
                        min={0}
                        max={365}
                        disabled={(org?.nonDeductibleLeaveTypes ?? []).includes(
                          type
                        )}
                        endAdornment={
                          <InputPostText variant="caption">
                            {t('perYear')}
                          </InputPostText>
                        }
                        onChange={(value: number) =>
                          updateOrg('leaveTypes', {
                            ...(org?.leaveTypes ?? {}),
                            [type]: value
                          })
                        }
                      />
                      <FormFieldWrapper>
                        <Switch
                          checked={
                            !(org?.disabledLeaveTypes ?? []).includes(type)
                          }
                          onChange={(
                            event: React.ChangeEvent<{ checked: boolean }>
                          ) => {
                            const currentLeaveTypes =
                              org?.disabledLeaveTypes ?? [];
                            if (event.target.checked) {
                              const nextDisabledLeaveTypes = currentLeaveTypes.filter(
                                currentType => currentType !== type
                              );
                              updateOrg(
                                'disabledLeaveTypes',
                                nextDisabledLeaveTypes
                              );
                            } else {
                              updateOrg('disabledLeaveTypes', [
                                ...currentLeaveTypes,
                                type
                              ]);
                            }
                          }}
                          inputProps={{
                            'aria-label': `${localizeLeaveType(t, type)} ${t(
                              'leaveTypeEnabled'
                            )}`
                          }}
                        />
                      </FormFieldWrapper>
                      <FormFieldWrapper>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                !(org?.nonDeductibleLeaveTypes ?? []).includes(
                                  type
                                )
                              }
                              onChange={(
                                event: React.ChangeEvent<{ checked: boolean }>
                              ) => {
                                const currentLeaveTypes =
                                  org?.nonDeductibleLeaveTypes ?? [];
                                if (event.target.checked) {
                                  updateOrg(
                                    'nonDeductibleLeaveTypes',
                                    currentLeaveTypes.filter(
                                      currentType => currentType !== type
                                    )
                                  );
                                } else {
                                  updateOrg('nonDeductibleLeaveTypes', [
                                    ...currentLeaveTypes,
                                    type
                                  ]);
                                }
                              }}
                              inputProps={{
                                'aria-label': `${localizeLeaveType(
                                  t,
                                  type
                                )} ${t('deductsAllowance')}`
                              }}
                            />
                          }
                          label={t('deductsAllowance')}
                        />
                      </FormFieldWrapper>
                      <IconButton
                        aria-label={`${t(
                          'removeLeaveType'
                        )} ${localizeLeaveType(t, type)}`}
                        title={
                          shouldKeepAtLeastOneLeaveType
                            ? t('keepAtLeastOneLeaveType')
                            : ''
                        }
                        onClick={() => onDeleteLeaveType(type)}
                        disabled={readOnly || shouldKeepAtLeastOneLeaveType}
                      >
                        <DeleteIcon color="secondary" />
                      </IconButton>
                    </Box>
                  </FormRow>
                  {(org?.nonDeductibleLeaveTypes ?? []).includes(type) && (
                    <FormRow>
                      <FormLabel />
                      <Typography variant="caption">
                        {t('leaveTypeNoBalanceImpact')}
                      </Typography>
                    </FormRow>
                  )}
                </Box>
              ))}
              {shouldKeepAtLeastOneLeaveType && (
                <FormRow>
                  <FormLabel />
                  <Typography variant="caption">
                    {t('keepAtLeastOneLeaveType')}
                  </Typography>
                </FormRow>
              )}
              <Gap />
              <TitleContainer>
                <Capitalized variant="h5">
                  {t('carryOverConfiguration')}
                </Capitalized>
              </TitleContainer>
              <Typography variant="body2">
                {t('carryOverConfigurationDescription')}
              </Typography>
              <Line />
              <FormRow>
                <FormLabel>{`${t('vacationCarryOverDays')}:`}</FormLabel>
                <FormNumberInput
                  initialValue={org?.leaveCarryOverDays ?? 0}
                  min={0}
                  max={30}
                  endAdornment={
                    <InputPostText variant="caption">{t('days')}</InputPostText>
                  }
                  onChange={(value: number) =>
                    updateOrg('leaveCarryOverDays', value)
                  }
                />
              </FormRow>
              <FormRow>
                <FormLabel>{`${t('vacationCarryOverExpiryMonths')}:`}</FormLabel>
                <FormNumberInput
                  initialValue={org?.leaveCarryOverExpiryMonths ?? 0}
                  min={0}
                  max={36}
                  endAdornment={
                    <InputPostText variant="caption">
                      {t('months')}
                    </InputPostText>
                  }
                  onChange={(value: number) =>
                    updateOrg('leaveCarryOverExpiryMonths', value)
                  }
                />
              </FormRow>
              <FormRow>
                <FormLabel>{`${t('autoApproveWhenNoManager')}:`}</FormLabel>
                <Switch
                  checked={org?.autoApproveWhenNoManager !== false}
                  onChange={event =>
                    updateOrg('autoApproveWhenNoManager', event.target.checked)
                  }
                />
              </FormRow>
            </>
          ),
          publicHolidays: (
            <>
              {readOnly ? (
                <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
              ) : null}
              <Typography variant="body2">
                {t('publicHolidaysDescription')}
              </Typography>
              <Line />
              <TitleContainer>
                <Capitalized variant="h6">{t('holidayProfile')}</Capitalized>
              </TitleContainer>
              <Typography variant="body2">
                {t('holidayProfileDescription')}
              </Typography>
              <FormRow>
                <FormLabel>{`${t('holidayProfile')}:`}</FormLabel>
                <Select
                  value={org?.holidayProfile?.country ?? 'none'}
                  onChange={event => {
                    const nextCountry = event.target.value as
                      | HolidayProfile['country']
                      | 'none';

                    return nextCountry === 'none'
                      ? updateOrg('holidayProfile', null)
                      : updateOrg(
                          'holidayProfile',
                          nextCountry === org?.holidayProfile?.country
                            ? holidayProfile
                            : createHolidayProfile(nextCountry)
                        );
                  }}
                >
                  <MenuItem value="none">{t('holidayProfileNone')}</MenuItem>
                  <MenuItem value="NL">{t('holidayProfileNL')}</MenuItem>
                  <MenuItem value="UK">{t('holidayProfileUK')}</MenuItem>
                  <MenuItem value="US">{t('holidayProfileUS')}</MenuItem>
                  <MenuItem value="ZA">{t('holidayProfileZA')}</MenuItem>
                  <MenuItem value="AU">{t('holidayProfileAU')}</MenuItem>
                  <MenuItem value="DK">{t('holidayProfileDK')}</MenuItem>
                  <MenuItem value="NO">{t('holidayProfileNO')}</MenuItem>
                  <MenuItem value="SE">{t('holidayProfileSE')}</MenuItem>
                </Select>
              </FormRow>
              {org?.holidayProfile?.country === 'NL' && (
                <>
                  <FormRow>
                    <FormLabel>{`${t('includeGoodFriday')}:`}</FormLabel>
                    <Switch
                      checked={!!holidayProfile.includeGoodFriday}
                      onChange={event =>
                        updateOrg('holidayProfile', {
                          ...holidayProfile,
                          includeGoodFriday: event.target.checked
                        })
                      }
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>{`${t('liberationDayMode')}:`}</FormLabel>
                    <Select
                      value={holidayProfile.liberationDayMode}
                      onChange={event =>
                        updateOrg('holidayProfile', {
                          ...holidayProfile,
                          liberationDayMode: event.target.value as
                            | 'every_year'
                            | 'every_5_years'
                        })
                      }
                    >
                      <MenuItem value="every_5_years">
                        {t('liberationDayEveryFiveYears')}
                      </MenuItem>
                      <MenuItem value="every_year">
                        {t('liberationDayEveryYear')}
                      </MenuItem>
                    </Select>
                  </FormRow>
                </>
              )}
              <Typography variant="body2">
                {t('holidayOverridesHint')}
              </Typography>
              <Gap />
              <Row mb={1}>
                <Spacer />
                <Button
                  color="primary"
                  startIcon={<AddIcon />}
                  aria-label="add"
                  onClick={onAddPublicHoliday}
                  disabled={readOnly}
                >
                  {t('addPublicHoliday')}
                </Button>
              </Row>
              <Table
                maxHeight="580px"
                headers={[t('date'), t('name'), '']}
                rows={(org?.publicHolidays ?? [])
                  .map(day => {
                    const holidayDate = publicHolidayDateForYear(
                      day.date,
                      publicHolidayDateReference
                    );
                    return {
                      ...day,
                      sortKey: holidayDate.isValid()
                        ? holidayDate.format('YYYY-MM-DD')
                        : day.date,
                      displayDate: holidayDate.isValid()
                        ? holidayDate.format(
                            isConcreteHolidayDate(day.date)
                              ? 'D MMMM YYYY'
                              : 'D MMMM'
                          )
                        : day.date
                    };
                  })
                  .sort((dayA, dayB) =>
                    dayA.sortKey.localeCompare(dayB.sortKey)
                  )
                  .map(day => ({
                    id: day.id,
                    disabled: !!day.disabled,
                    cells: [
                      day.displayDate,
                      <>
                        {day.name}
                        {day.disabled && ` (${t('disabledHoliday')})`}
                      </>,
                      <>
                        <IconButton
                          onClick={() =>
                            onEditPublicHoliday(day.id, day.date, day.name)
                          }
                          disabled={readOnly}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => onDeletePublicHoliday(day.id)}
                          disabled={readOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    ]
                  }))}
              />
              <EditPublicHolidayDialog
                title={t('editPublicHolidayTitle')}
                text={t('editPublicHolidaySubtitle')}
                initialValue={{
                  name: publicHolidayDialog?.name,
                  date: publicHolidayDialog?.date
                }}
                dialogOpen={!!publicHolidayDialog}
                onClose={() => setPublicHolidayDialog(null)}
                onSubmit={publicHolidayDialog?.callback}
              />
            </>
          )
        }
      : {}),
    ...(orgFeatures.expenses
      ? {
          expenseConfig: (
            <>
              {readOnly ? (
                <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
              ) : null}
              <Typography variant="body2">
                {t('expenseConfigDescription')}
              </Typography>
              <Line />
              <FormRow>
                <FormLabel>{`${t('expenseInboxEmail')}:`}</FormLabel>
                <FormTextInput
                  initialValue={org?.expenseInboxEmail}
                  validate={validateEmail}
                  onChange={(value: string) =>
                    updateOrg('expenseInboxEmail', value)
                  }
                />
              </FormRow>
              <FormRow>
                <FormLabel>{`${t('expenseKmRate')}:`}</FormLabel>
                <FormNumberInput
                  initialValue={org?.expenseKmRate ?? 0.23}
                  min={0}
                  onChange={(value: number) =>
                    updateOrg('expenseKmRate', value)
                  }
                />
              </FormRow>
              <FormRow>
                <FormLabel>{`${t('expenseIdPrefix')}:`}</FormLabel>
                <FormTextInput
                  initialValue={org?.expenseIdPrefix}
                  validate={validateExpenseIdPrefix}
                  inputProps={{ maxLength: 12 }}
                  onChange={(value: string) =>
                    updateOrg('expenseIdPrefix', value)
                  }
                />
              </FormRow>
            </>
          )
        }
      : {}),
    hourConfig: (
      <>
        {readOnly ? (
          <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
        ) : null}
        <Typography variant="body2">{t('orgHoursSettings')}</Typography>
        <Line />
        <Gap />
        <FormRow>
          <FormLabel>{`${t('orgHoursPerDay')}:`}</FormLabel>
          <FormNumberInput
            initialValue={org?.hoursPerDay}
            onChange={(value: number) => updateOrg('hoursPerDay', value)}
          />
        </FormRow>
        <FormRow>
          <FormLabel>{`${t('orgHoursPerWeek')}:`}</FormLabel>
          <FormNumberInput
            initialValue={org?.hoursPerWeek}
            onChange={(value: number) => updateOrg('hoursPerWeek', value)}
          />
        </FormRow>
      </>
    ),
    billing: (
      <>
        <Line />

        {org?.stripe ? (
          <Box mb={3}>
            <FormRow>
              <FormLabel>{t('status')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${
                  org.stripe.status ? t(org.stripe.status) : t('unknown')
                }`}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('accessMode')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={t(
                  org.stripe.accessMode === 'read_only'
                    ? 'readOnlyAccess'
                    : 'fullAccess'
                )}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('trialEnd')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${
                  org.stripe.trialEndsAt
                    ? moment(org.stripe.trialEndsAt).format('LL')
                    : t('notAvailable')
                }`}
              />
            </FormRow>
            {org.stripe.readOnlyReason ? (
              <FormRow>
                <Typography color="error" variant="body2">
                  {t('trialReadOnlyBanner')}
                </Typography>
              </FormRow>
            ) : null}
            {billingRestoreResult === 'success' ? (
              <FormRow>
                <Typography color="primary" variant="body2">
                  {t('billingRestoreStarted')}
                </Typography>
              </FormRow>
            ) : null}
            {billingRestoreResult === 'canceled' ? (
              <FormRow>
                <Typography variant="body2">
                  {t('billingRestoreCanceled')}
                </Typography>
              </FormRow>
            ) : null}
            {org.stripe.accessMode === 'read_only' ? (
              <FormRow>
                <Spacer />
                <Button
                  color="primary"
                  variant="contained"
                  onClick={onRestoreBilling}
                  disabled={billingRestorePending || loading}
                >
                  {billingRestorePending ? t('loading') : t('restoreBilling')}
                </Button>
              </FormRow>
            ) : null}
          </Box>
        ) : null}

        {org?.subscriptions?.map(sub => (
          <Box key={sub.startDate}>
            <FormRow>
              <FormLabel>{t('startDate')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${moment(sub.startDate).format('LL') ?? ''}`}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('lastBillDate')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${moment(sub.lastBillDate).format('LL') ?? ''}`}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('nextBillDate')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${moment(sub.nextBillDate).format('LL') ?? ''}`}
              />
            </FormRow>
            <FormRow>
              <FormLabel>{t('status')}</FormLabel>
              <FormTextInput
                disabled
                initialValue={`${sub.status ? t(sub.status) : t('unknown')}`}
              />
            </FormRow>
            <Gap />
            <Table
              headers={[t('product'), t('price')]}
              rows={sub.products.map(product => ({
                id: product.name,
                cells: [
                  t(product.name),
                  formatNumber(product.price / 100, {
                    currency: product.currency,
                    style: 'currency'
                  })
                ]
              }))}
            />
          </Box>
        ))}
        <TitleContainer>
          <Capitalized variant="h5">{t('dangerZone')}</Capitalized>
        </TitleContainer>
        <Line />
        <FormRow>
          <Typography variant="body2">{t('deleteOrganizationDesc')}</Typography>
          <Spacer />
          <Button
            variant="contained"
            color="secondary"
            onClick={onDeleteOrganization}
            disabled={readOnly}
          >
            {t('deleteOrganization')}
          </Button>
        </FormRow>
        <PermanentlyDeleteDialog
          title={t('deleteOrganizationTitle')}
          text={t('deleteOrganizationSubtitle')}
          validationWord={organization}
          dialogOpen={!!deleteDialog}
          onClose={() => setDeleteDialog(null)}
          onSubmit={deleteDialog?.callback}
        />
      </>
    )
  };

  return (
    <>
      <Container>
        <Tabs
          selected={tab}
          onTabChange={handleTabChange}
          loading={loading}
          tabs={tabs}
        />
      </Container>
      <ConfirmDialog
        title={confirmDialog?.title ?? t('confirmPublicHolidayDeleteTitle')}
        text={confirmDialog?.text ?? t('confirmPublicHolidayDeleteSubtitle')}
        dialogOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onSubmit={confirmDialog?.callback}
      />
    </>
  );
}
